/**
 * Single source of truth for module-level access control.
 *
 * Each module maps a permission `key` (must match the backend User::MODULES keys) to:
 *  - `label`     : human-readable name shown on the User Access Control cards
 *  - `menuIds`   : LeftMenu item ids that belong to this module (hidden when access is denied)
 *  - `paths`     : route path prefixes guarded by <ModuleRoute> (typing the URL is blocked too)
 *
 * `authorize_action` is a special capability (not a page): it controls whether the
 * Authorize / Unauthorize CTAs are shown on the authorization screens.
 */
export const MODULES = [
  {
    key: 'company_master',
    label: 'Company Master',
    menuIds: ['company_details'],
    paths: ['/dashboard/company-details'],
  },
  {
    key: 'crm_master',
    label: 'CRM Master',
    menuIds: ['crm-master'],
    paths: ['/dashboard/crm-master'],
  },
  {
    key: 'product_master',
    label: 'Product Master',
    menuIds: ['product-master'],
    paths: ['/dashboard/product/master'],
  },
  {
    key: 'general_master',
    label: 'General Master',
    menuIds: ['general-master'],
    paths: ['/dashboard/general-master'],
  },
  {
    key: 'add_product',
    label: 'Add Product',
    menuIds: ['pm-add-product'],
    paths: ['/dashboard/product/add-product', '/dashboard/product/edit-product'],
  },
  {
    key: 'catalogue',
    label: 'Catalogue',
    menuIds: ['pm-catalog'],
    paths: ['/dashboard/product/catalog'],
  },
  {
    key: 'client_catalogue',
    label: 'Client Catalogue',
    menuIds: ['pm-client-catalog'],
    paths: ['/dashboard/product/client-catalog'],
  },
  {
    key: 'view_cart',
    label: 'View Cart',
    menuIds: ['pm-cart'],
    paths: ['/dashboard/product/cart'],
  },
  {
    key: 'product_authorization',
    label: 'Product Authorization',
    menuIds: ['product-authorization'],
    paths: ['/dashboard/product/authorize'],
  },
  {
    key: 'order_authorization',
    label: 'Order Authorization',
    menuIds: ['order-authorization'],
    paths: ['/dashboard/order/authorize'],
  },
  {
    key: 'user_access_control',
    label: 'User Access Control',
    menuIds: ['user-access-control'],
    paths: ['/dashboard/user/access-control'],
  },
  {
    key: 'proforma_invoice',
    label: 'Proforma Invoice',
    menuIds: ['proforma'],
    paths: ['/dashboard/product/proforma-invoice'],
  },
  {
    key: 'order_creation',
    label: 'Order Creation',
    menuIds: ['order-creation'],
    paths: ['/dashboard/order/create', '/dashboard/order/edit'],
  },
  {
    key: 'order_search',
    label: 'Order Search',
    menuIds: ['order-search'],
    paths: ['/dashboard/order/search'],
  },
  {
    key: 'dms',
    label: 'DMS',
    menuIds: ['dms'],
    paths: ['/dashboard/dms'],
  },
  {
    key: 'authorize_action',
    label: 'Authorize / Unauthorize action',
    menuIds: [],
    paths: [],
  },
];

/** Menu ids that are always visible regardless of permissions (e.g. the Dashboard home). */
export const ALWAYS_VISIBLE_MENU_IDS = ['welcome'];

/** menuId -> module key (only for ids that directly gate a module). */
export const MENU_ID_TO_MODULE = MODULES.reduce((acc, m) => {
  m.menuIds.forEach((id) => {
    acc[id] = m.key;
  });
  return acc;
}, {});

/**
 * Resolve the module key that guards a given route path, or null if the path is ungated.
 * Longest matching prefix wins so `/order/edit` beats a hypothetical `/order`.
 */
export const moduleKeyForPath = (pathname) => {
  let best = null;
  let bestLen = -1;
  MODULES.forEach((m) => {
    m.paths.forEach((p) => {
      if ((pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(p)) && p.length > bestLen) {
        best = m.key;
        bestLen = p.length;
      }
    });
  });
  return best;
};
